import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const UniversitySignupForm = ({ onSubmit, loading }) => {
 const [formData, setFormData] = useState({
 universityName: '',
 contactPersonName: '',
 email: '',
 phoneNumber: '',
 universityTypeId: '',
 cityId: '',
 address: '',
 establishedYear: '',
 password: '',
 confirmPassword: ''
 });

 const [showPassword, setShowPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 const [errors, setErrors] = useState({});
 const [universityTypes, setUniversityTypes] = useState([]);
 const [cities, setCities] = useState([]);

 // Fetch university types and cities on component mount
 useEffect(() => {
 fetchUniversityTypes();
 fetchCities();
 }, []);

 const fetchUniversityTypes = async () => {
 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/university-types`);
 if (response.ok) {
 const data = await response.json();
 setUniversityTypes(data);
 }
 } catch (error) {
 console.error('Error fetching university types:', error);
 }
 };

 const fetchCities = async () => {
 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/cities`);
 if (response.ok) {
 const data = await response.json();
 setCities(data);
 }
 } catch (error) {
 console.error('Error fetching cities:', error);
 }
 };

 const handleChange = (e) => {
 const { name, value } = e.target;
 
 // Special handling for phone number - only allow digits and limit to 11
 if (name === 'phoneNumber') {
 const cleanValue = value.replace(/\D/g, '');
 if (cleanValue.length <= 11) {
 setFormData(prev => ({
 ...prev,
 [name]: cleanValue
 }));
 }
 }
 else {
 setFormData(prev => ({
 ...prev,
 [name]: value
 }));
 }
 
 // Clear error when user starts typing
 if (errors[name]) {
 setErrors(prev => ({
 ...prev,
 [name]: ''
 }));
 }
 };

 const validateForm = () => {
 const newErrors = {};

 // Required field validation
 const requiredFields = [
 'universityName', 'contactPersonName', 'email', 'phoneNumber', 
 'address', 'establishedYear', 'password', 'confirmPassword'
 ];

 requiredFields.forEach(field => {
 if (!formData[field] || !formData[field].toString().trim()) {
 newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
 }
 });

 // Email validation - strict format
 const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
 if (formData.email && !emailRegex.test(formData.email)) {
 newErrors.email = 'Please enter a valid email address (e.g., university@example.edu)';
 }

 // Phone validation (Pakistani format: 03XXXXXXXXX)
 const phoneRegex = /^03\d{9}$/;
 const cleanPhone = formData.phoneNumber.replace(/\s/g, '');
 if (formData.phoneNumber && !phoneRegex.test(cleanPhone)) {
 newErrors.phoneNumber = 'Phone number must be 11 digits starting with 03 (e.g., 03001234567)';
 }

 // Established year validation
 const currentYear = new Date().getFullYear();
 const year = parseInt(formData.establishedYear);
 if (formData.establishedYear && (isNaN(year) || year < 1800 || year > currentYear)) {
 newErrors.establishedYear = `Year must be between 1800 and ${currentYear}`;
 }

 // Password validation
 if (formData.password && formData.password.length < 8) {
 newErrors.password = 'Password must be at least 8 characters long';
 }

 // Confirm password validation
 if (formData.password !== formData.confirmPassword) {
 newErrors.confirmPassword = 'Passwords do not match';
 }

 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (validateForm()) {
 onSubmit(formData);
 }
 };

 return (
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* University Information */}
 <div className="bg-slate-50 p-4 rounded-lg">
 <h3 className="text-lg font-semibold text-slate-900 mb-4">University Information</h3>
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">
 University Name *
 </label>
 <input
 type="text"
 name="universityName"
 value={formData.universityName}
 onChange={handleChange}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
 errors.universityName ? 'border-red-500' : 'border-slate-300'
 }`}
 placeholder="Enter university name"
 />
 {errors.universityName && (
 <p className="text-red-500 text-xs mt-1">{errors.universityName}</p>
 )}
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">
 University Type
 </label>
 <select
 name="universityTypeId"
 value={formData.universityTypeId}
 onChange={handleChange}
 className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
 >
 <option value="">Select university type</option>
 {universityTypes.map(type => (
 <option key={type.id} value={type.id}>
 {type.name}
 </option>
 ))}
 </select>
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">
 Established Year *
 </label>
 <input
 type="number"
 name="establishedYear"
 value={formData.establishedYear}
 onChange={handleChange}
 min="1800"
 max={new Date().getFullYear()}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
 errors.establishedYear ? 'border-red-500' : 'border-slate-300'
 }`}
 placeholder="e.g., 1995"
 />
 {errors.establishedYear && (
 <p className="text-red-500 text-xs mt-1">{errors.establishedYear}</p>
 )}
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">
 City
 </label>
 <select
 name="cityId"
 value={formData.cityId}
 onChange={handleChange}
 className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
 >
 <option value="">Select city</option>
 {cities.map(city => (
 <option key={city.id} value={city.id}>
 {city.name}
 </option>
 ))}
 </select>
 </div>
 </div>

 <div className="mt-4">
 <label className="block text-sm font-medium text-slate-700 mb-1">
 Address *
 </label>
 <textarea
 name="address"
 value={formData.address}
 onChange={handleChange}
 rows="3"
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
 errors.address ? 'border-red-500' : 'border-slate-300'
 }`}
 placeholder="Enter complete university address"
 />
 {errors.address && (
 <p className="text-red-500 text-xs mt-1">{errors.address}</p>
 )}
 </div>
 </div>

 {/* Contact Information */}
 <div className="bg-slate-50 p-4 rounded-lg">
 <h3 className="text-lg font-semibold text-slate-900 mb-4">Contact Information</h3>
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">
 Contact Person Name *
 </label>
 <input
 type="text"
 name="contactPersonName"
 value={formData.contactPersonName}
 onChange={handleChange}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
 errors.contactPersonName ? 'border-red-500' : 'border-slate-300'
 }`}
 placeholder="e.g., Dr. John Smith"
 />
 {errors.contactPersonName && (
 <p className="text-red-500 text-xs mt-1">{errors.contactPersonName}</p>
 )}
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">
 Email Address *
 </label>
 <input
 type="email"
 name="email"
 value={formData.email}
 onChange={handleChange}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
 errors.email ? 'border-red-500' : 'border-slate-300'
 }`}
 placeholder="admin@university.edu.pk"
 />
 {errors.email && (
 <p className="text-red-500 text-xs mt-1">{errors.email}</p>
 )}
 <p className="text-slate-500 text-xs mt-1">Must be a valid email format</p>
 </div>

 <div className="md:col-span-2">
 <label className="block text-sm font-medium text-slate-700 mb-1">
 Phone Number *
 </label>
 <input
 type="tel"
 name="phoneNumber"
 value={formData.phoneNumber}
 onChange={handleChange}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
 errors.phoneNumber ? 'border-red-500' : 'border-slate-300'
 }`}
 placeholder="03001234567"
 maxLength="11"
 pattern="03\d{9}"
 />
 {errors.phoneNumber && (
 <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
 )}
 <p className="text-slate-500 text-xs mt-1">11 digits starting with 03</p>
 </div>
 </div>
 </div>

 {/* Security */}
 <div className="bg-slate-50 p-4 rounded-lg">
 <h3 className="text-lg font-semibold text-slate-900 mb-4">Security</h3>
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">
 Password *
 </label>
 <div className="relative">
 <input
 type={showPassword ? 'text' : 'password'}
 name="password"
 value={formData.password}
 onChange={handleChange}
 className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
 errors.password ? 'border-red-500' : 'border-slate-300'
 }`}
 placeholder="Enter password"
 />
 <button
 type="button"
 onClick={() => setShowPassword(!showPassword)}
 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
 >
 {showPassword ? (
 <EyeSlashIcon className="h-5 w-5" />
 ) : (
 <EyeIcon className="h-5 w-5" />
 )}
 </button>
 </div>
 {errors.password && (
 <p className="text-red-500 text-xs mt-1">{errors.password}</p>
 )}
 </div>

 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">
 Confirm Password *
 </label>
 <div className="relative">
 <input
 type={showConfirmPassword ? 'text' : 'password'}
 name="confirmPassword"
 value={formData.confirmPassword}
 onChange={handleChange}
 className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
 errors.confirmPassword ? 'border-red-500' : 'border-slate-300'
 }`}
 placeholder="Confirm password"
 />
 <button
 type="button"
 onClick={() => setShowConfirmPassword(!showConfirmPassword)}
 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
 >
 {showConfirmPassword ? (
 <EyeSlashIcon className="h-5 w-5" />
 ) : (
 <EyeIcon className="h-5 w-5" />
 )}
 </button>
 </div>
 {errors.confirmPassword && (
 <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
 )}
 </div>
 </div>
 </div>

 {/* Submit Button */}
 <button
 type="submit"
 disabled={loading}
 className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
 >
 {loading ? 'Creating Account...' : 'Create University Account'}
 </button>
 </form>
 );
};

export default UniversitySignupForm;
